import { StorageAdapter } from "@urql/exchange-graphcache";
import { createContext, useContext, useSyncExternalStore } from "react";

/** Number of mutations that wait in the queue of the `offlineExchange` until the device is online again. */
export interface OfflineQueue {
  readonly size: () => number;
  readonly subscribe: (listener: () => void) => () => void;
}

/**
 * Observes the offline queue through the storage: the `offlineExchange` writes the whole queue with `writeMetadata`
 * whenever it changes, and has no public API for it.
 */
export function observeOfflineQueue<S extends StorageAdapter>(
  storage: S,
): { storage: S; offlineQueue: OfflineQueue } {
  let size = 0;
  const listeners = new Set<() => void>();
  const setSize = (newSize: number) => {
    if (newSize !== size) {
      size = newSize;
      listeners.forEach((l) => l());
    }
  };

  const observedStorage: S = {
    ...storage,
    readMetadata: () =>
      (storage.readMetadata?.() ?? Promise.resolve(null)).then((queue) => {
        setSize(queue?.length ?? 0);
        return queue;
      }),
    writeMetadata: (queue) => {
      setSize(queue.length);
      storage.writeMetadata?.(queue);
    },
  };

  return {
    storage: observedStorage,
    offlineQueue: {
      size: () => size,
      subscribe: (listener) => {
        listeners.add(listener);
        return () => listeners.delete(listener);
      },
    },
  };
}

const emptyQueue: OfflineQueue = { size: () => 0, subscribe: () => () => {} };

export const OfflineQueueContext = createContext<OfflineQueue>(emptyQueue);

/** Number of mutations waiting in the offline queue, re-rendered on every change. */
export function useOfflineQueueSize(): number {
  const offlineQueue = useContext(OfflineQueueContext);
  return useSyncExternalStore(offlineQueue.subscribe, offlineQueue.size);
}
